import { Form, FormikProvider, useFormik } from "formik";
import React from "react";
import { Class } from "@common/types/class";
import { submitHelper } from "@helpers/form";
import { checkClassLabelConflict } from "@helpers/class";
import { useClasses } from "@api/class.api";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Button } from "@design-system/Button/Button";
import { StandardModalHeader } from "@design-system/Modal/Modal";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { ClassTreeSelectField } from "@components/forms/ClassTreeSelectField/ClassTreeSelectField";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";

export interface ClassSchema {
    name: string;
    parentClassId: string | null;
}

const fields = createFormikFields<ClassSchema>();

interface ClassFormProps {
    classInstance?: Class;
    onHide: () => void;
    isPending: boolean;
    onSubmitClass: (data: ClassSchema) => Promise<unknown>;
    parentClassId: string | null;
}

export const ClassForm: React.FC<ClassFormProps> = ({
    classInstance,
    onHide,
    isPending,
    onSubmitClass,
    parentClassId,
}) => {
    const { classes, classesMap, classesWithChildren } = useClasses();

    const shouldShowParentPicker = !classInstance;

    const formik = useFormik<ClassSchema>({
        initialValues: {
            name: classInstance?.label ?? "",
            parentClassId: classInstance?.parentClassId ?? parentClassId,
        },
        validate: (values) => {
            const errors: Partial<ClassSchema> = {};

            if (!values.name) {
                errors.name = "Required";
            }

            const { conflict } = checkClassLabelConflict({
                classes,
                parentClassId:
                    classInstance?.parentClassId ??
                    values.parentClassId ??
                    null,
                label: values.name,
                targetClassId: classInstance?.id,
            });

            if (conflict) {
                errors.name = "A class with this name already exists here";
            }

            return errors;
        },
        validateOnMount: true,
        onSubmit: submitHelper({
            loading: isPending,
            handler: async (data) => {
                await onSubmitClass({
                    name: data.name,
                    parentClassId: data.parentClassId,
                });
                onHide();
            },
        }),
    });

    return (
        <>
            <StandardModalHeader>
                {classInstance ? "Update " : "Create "}
                {parentClassId ? "subclass" : "class"}
            </StandardModalHeader>
            <FormikProvider value={formik}>
                <Form>
                    <VStack gap={16}>
                        {shouldShowParentPicker ? (
                            <VStack gap={16}>
                                <FormikField<string | null>
                                    name="parentClassId"
                                    label="Parent class"
                                    control="button"
                                >
                                    {({ field, helpers }) => (
                                        <ClassTreeSelectField
                                            value={field.value}
                                            onChange={(id) => {
                                                void helpers.setValue(id);
                                            }}
                                            classesWithChildren={
                                                classesWithChildren
                                            }
                                            classesMap={classesMap}
                                            placeholder="No parent (top-level class)"
                                        />
                                    )}
                                </FormikField>
                                <fields.Text
                                    name="name"
                                    label="Name"
                                    size="xl"
                                />
                            </VStack>
                        ) : (
                            <fields.Text name="name" label="Name" size="xl" />
                        )}

                        <HStack as="footer" justify="end" gap={12}>
                            <Button
                                variant="secondary"
                                size="xl"
                                type="button"
                                onClick={onHide}
                                disabled={isPending}
                            >
                                Cancel
                            </Button>

                            <ButtonWithLoader
                                variant="primary"
                                size="xl"
                                loading={isPending}
                                disabled={!formik.isValid}
                                hotKey="mod+enter"
                                hotKeyOptions={{
                                    description: "Save class",
                                    preventDefault: true,
                                    enableOnFormTags: true,
                                }}
                            >
                                {classInstance ? "Save" : "Create"}
                            </ButtonWithLoader>
                        </HStack>
                    </VStack>
                </Form>
            </FormikProvider>
        </>
    );
};
